// Holder for the raw File handles, keyed by submissionID then file name. File objects are not
// serialisable so they must stay out of Redux; the slice only stores name/size/status metadata.
// The handles are lost on a hard refresh or on leaving the wizard — the server copy is the source
// of truth, so consumers that need the bytes use getOrDownloadSubmissionFile, which falls back to
// the DownloadFile endpoint and re-caches the handle here.

import { IDataImportService } from "services/DataImportService";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

const files = new Map<string, Map<string, File>>();

export function setSubmissionFile(submissionID: string, file: File): void {
  if (!files.has(submissionID)) {
    files.set(submissionID, new Map());
  }
  files.get(submissionID)?.set(file.name, file);
}

export function getSubmissionFile(submissionID: string, fileName: string): File | undefined {
  return files.get(submissionID)?.get(fileName);
}

// Local handle if we still have it, otherwise re-download from the server (and cache the handle
// for the rest of the session). undefined = no local handle and no server row to fetch.
export async function getOrDownloadSubmissionFile(
  submissionID: string,
  fileName: string,
  submissionFileID?: string
): Promise<File | undefined> {
  const local = getSubmissionFile(submissionID, fileName);
  if (local) {
    return local;
  }
  if (!submissionFileID) {
    return undefined;
  }
  const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
  const response = await dataImportService.downloadSubmissionFile(submissionFileID);
  const file = new File([response.data], fileName);
  setSubmissionFile(submissionID, file);
  return file;
}

export function clearSubmissionFile(submissionID: string, fileName: string): void {
  files.get(submissionID)?.delete(fileName);
}

export function clearSubmissionFiles(submissionID: string): void {
  files.delete(submissionID);
}
